import type { ComputedRef } from 'vue';
import { computed, ref } from 'vue';
import type { Ticket, VoucherCapableTicket } from '@/interface/ITicket';
import { TicketType, TicketVoucherOptionStatus } from '@/interface/enum';
import type { IVoucher, IVoucherDisplay, IVoucherInput } from '@/interface/voucher';
import { assertNever, getTicketPrice, isVoucherCapable } from '@/libraries/betSlip/ticketHelper';
import useState from '@/libraries/composables/useState';
import { buildVoucherDisplayList } from '@/libraries/composables/useVoucher';

export interface IVoucherRestoreInput {
    isRedeem: boolean;
    selections: Record<string, string>;
}

export interface ITicketVoucherOption {
    id: string;
    text: string;
    status: TicketVoucherOptionStatus;
}

export function createTicketVoucherInput(ticket: VoucherCapableTicket): IVoucherInput {
    switch (ticket.type) {
        case TicketType.Single:
            return {
                sportType: ticket.event.sportType,
                leagueId: ticket.event.leagueId,
                minBet: ticket.minBet,
                maxBet: ticket.maxBet,
                marketType: ticket.odds.marketType,
                price: getTicketPrice(ticket),
                priceStyle: ticket.priceStyle,
            };
        case TicketType.DynamicMarket:
            return {
                sportType: ticket.match.sportType,
                leagueId: ticket.match.leagueId,
                minBet: ticket.minBet,
                maxBet: ticket.maxBet,
                marketType: ticket.market.id,
                price: getTicketPrice(ticket),
                priceStyle: ticket.priceStyle,
            };
        default:
            return assertNever(ticket);
    }
}

export function useSingleModeVoucher(tickets: ComputedRef<Ticket[]>) {
    const [isRedemptionEnabled, setIsRedemptionEnabled] = useState(false);
    const rawVouchersByTicket = ref<Record<string, IVoucher[]>>({});
    const voucherSelections = ref<Record<string, string>>({});

    const selectedVoucherIds = computed<Set<string>>(() => new Set(Object.values(voucherSelections.value)));
    const readonlyVoucherSelections = computed<Record<string, string>>(() => ({ ...voucherSelections.value }));

    const ticketVouchersByKey = computed<Record<string, IVoucherDisplay[]>>(() => Object.fromEntries(
        tickets.value.map(ticket => [ticket.key, buildTicketVouchers(ticket)]),
    ));

    const hasAvailableVoucher = computed<boolean>(() => Object.values(ticketVouchersByKey.value)
        .some(vouchers => vouchers.some(voucher => voucher.isAvailable)));

    function buildTicketVouchers(ticket: Ticket): IVoucherDisplay[] {
        if (!isVoucherCapable(ticket)) {
            return [];
        }
        const rawVouchers = rawVouchersByTicket.value[ticket.key] ?? [];
        if (rawVouchers.length === 0) {
            return [];
        }
        return buildVoucherDisplayList(rawVouchers, createTicketVoucherInput(ticket));
    }

    function applyTicketVouchers(key: string, rawVouchers: IVoucher[] | null) {
        // null means the Voucher fetch failed; keep the previous list.
        if (rawVouchers === null) {
            return;
        }

        rawVouchersByTicket.value[key] = rawVouchers;
    }

    function getTicketVouchers(ticket: Ticket): IVoucherDisplay[] {
        return ticketVouchersByKey.value[ticket.key] ?? [];
    }

    function getTicketVoucherOptionStatus(
        voucher: IVoucherDisplay,
        selectedVoucherId: string | null,
        selectedIds: Set<string>,
    ): TicketVoucherOptionStatus {
        if (voucher.id === selectedVoucherId) {
            return TicketVoucherOptionStatus.Selected;
        }
        if (selectedIds.has(voucher.id)) {
            return TicketVoucherOptionStatus.SelectedByOtherTicket;
        }
        if (voucher.unavailableReason) {
            return voucher.unavailableReason;
        }
        return TicketVoucherOptionStatus.Available;
    }

    function buildTicketVoucherOptions(ticket: Ticket): ITicketVoucherOption[] {
        const selectedVoucherId = getSelectedVoucherId(ticket.key);
        return getTicketVouchers(ticket).map(voucher => ({
            id: voucher.id,
            text: voucher.text,
            status: getTicketVoucherOptionStatus(voucher, selectedVoucherId, selectedVoucherIds.value),
        }));
    }

    const ticketVoucherOptionsByKey = computed<Record<string, ITicketVoucherOption[]>>(() => Object.fromEntries(
        tickets.value.map(ticket => [ticket.key, buildTicketVoucherOptions(ticket)]),
    ));

    function getTicketVoucherOptions(ticket: Ticket): ITicketVoucherOption[] {
        return ticketVoucherOptionsByKey.value[ticket.key] ?? [];
    }

    function getSelectedVoucherId(key: string): string | null {
        return voucherSelections.value[key] ?? null;
    }

    function getSelectedVoucher(ticket: Ticket): IVoucherDisplay | null {
        const selectedId = getSelectedVoucherId(ticket.key);
        if (selectedId === null) {
            return null;
        }
        return getTicketVouchers(ticket).find(voucher => voucher.id === selectedId) ?? null;
    }

    function getSelectedVoucherStake(ticket: Ticket): number | null {
        return getSelectedVoucher(ticket)?.stake ?? null;
    }

    function isTicketRedeemable(ticket: Ticket): boolean {
        return getSelectedVoucher(ticket)?.isAvailable === true;
    }

    function selectVoucher(ticket: VoucherCapableTicket, voucherId: string): boolean {
        const isUsedByOtherTicket = selectedVoucherIds.value.has(voucherId)
            && getSelectedVoucherId(ticket.key) !== voucherId;
        if (isUsedByOtherTicket) {
            return false;
        }
        const isRedeemable = getTicketVouchers(ticket)
            .some(voucher => voucher.id === voucherId && voucher.isAvailable);
        if (!isRedeemable) {
            return false;
        }
        voucherSelections.value[ticket.key] = voucherId;
        return true;
    }

    function clearVoucher(key: string) {
        delete voucherSelections.value[key];
    }

    function setRedemptionEnabled(value: boolean) {
        setIsRedemptionEnabled(value);
        voucherSelections.value = {};
    }

    function restoreVoucherState({ isRedeem, selections }: IVoucherRestoreInput) {
        setIsRedemptionEnabled(isRedeem);
        voucherSelections.value = { ...selections };
    }

    function removeTicketVouchers(key: string) {
        delete rawVouchersByTicket.value[key];
        clearVoucher(key);
    }

    function removeAllTicketVouchers() {
        rawVouchersByTicket.value = {};
        voucherSelections.value = {};
    }

    return {
        isRedemptionEnabled,
        hasAvailableVoucher,
        voucherSelections: readonlyVoucherSelections,
        selectedVoucherIds,
        applyTicketVouchers,
        getTicketVouchers,
        getTicketVoucherOptions,
        getSelectedVoucherId,
        getSelectedVoucher,
        getSelectedVoucherStake,
        isTicketRedeemable,
        selectVoucher,
        clearVoucher,
        setRedemptionEnabled,
        restoreVoucherState,
        removeTicketVouchers,
        removeAllTicketVouchers,
    };
}
